'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Users, Calendar, Compass, Heart, CheckCircle, ArrowRight, ArrowLeft, Star, Plane, Hotel, Car, FileCheck, UserCheck, Download, Send, RotateCcw, Phone, Mail, Loader2, AlertCircle, Edit3, Sparkles } from 'lucide-react';

const STEPS = [
  { key: 'step-travelers', num: 1, label: 'Jamaah', icon: <Users size={14} /> },
  { key: 'step-type', num: 2, label: 'Jenis', icon: <Compass size={14} /> },
  { key: 'step-dates', num: 3, label: 'Tanggal', icon: <Calendar size={14} /> },
  { key: 'step-stay', num: 4, label: 'Tinggal', icon: <Hotel size={14} /> },
  { key: 'step-flight', num: 5, label: 'Penerbangan', icon: <Plane size={14} /> },
  { key: 'step-transport', num: 6, label: 'Transport', icon: <Car size={14} /> },
  { key: 'step-priorities', num: 7, label: 'Prioritas', icon: <Heart size={14} /> },
  { key: 'step-requests', num: 8, label: 'Permintaan', icon: <Edit3 size={14} /> },
  { key: 'step-review', num: 9, label: 'Review', icon: <CheckCircle size={14} /> },
];

const journeyTypes = [
  { key: 'jt-private-umrah', id: 'PRIVATE_UMRAH', label: 'Private Umrah', desc: 'Umrah eksklusif dengan layanan personal penuh, tanpa bergabung grup lain.', icon: '🕌' },
  { key: 'jt-family-umrah', id: 'FAMILY_UMRAH', label: 'Family Umrah', desc: 'Dirancang khusus untuk keluarga dengan anak-anak dan lansia.', icon: '👨‍👩‍👧‍👦' },
  { key: 'jt-couple-umrah', id: 'COUPLE_UMRAH', label: 'Couple Umrah', desc: 'Perjalanan spiritual berdua yang intim dan penuh makna.', icon: '💑' },
  { key: 'jt-umrah-journey', id: 'UMRAH_JOURNEY', label: 'Umrah + Muslim Journey', desc: 'Gabungkan Umrah dengan destinasi Muslim lainnya.', icon: '✈️' },
  { key: 'jt-custom', id: 'CUSTOM_JOURNEY', label: 'Custom Journey', desc: 'Tidak ada template. Kami rancang dari nol sesuai keinginan Anda.', icon: '⭐' },
];

const journeyStyles = [
  { key: 'style-essential', id: 'ESSENTIAL', label: 'Essential', tag: 'Best Value', desc: 'Hotel bintang 4, penerbangan ekonomi, transport bersama. Fokus pada ibadah dengan harga terjangkau.', color: 'border-blue-200 bg-blue-50/50' },
  { key: 'style-signature', id: 'SIGNATURE', label: 'Signature', tag: 'Best Balance', desc: 'Hotel bintang 5 near Haram, penerbangan ekonomi/bisnis, transport private. Keseimbangan sempurna.', color: 'border-primary/30 bg-primary/5', recommended: true },
  { key: 'style-private', id: 'PRIVATE', label: 'Private', tag: 'Max Comfort', desc: 'Hotel bintang 5 Haram view, business class, private car, muttawif eksklusif. Kenyamanan maksimal.', color: 'border-accent/40 bg-accent/5' },
  { key: 'style-bespoke', id: 'BESPOKE', label: 'Bespoke', tag: 'Fully Custom', desc: 'Tidak ada template. Setiap komponen dirancang dari nol sesuai keinginan dan kebutuhan Anda.', color: 'border-purple-200 bg-purple-50/50' },
];

const origins = [
  { key: 'origin-jkt', label: 'Jakarta', code: 'CGK' },
  { key: 'origin-sby', label: 'Surabaya', code: 'SUB' },
  { key: 'origin-bdg', label: 'Bandung', code: 'BDO' },
  { key: 'origin-mdn', label: 'Medan', code: 'KNO' },
  { key: 'origin-mks', label: 'Makassar', code: 'UPG' },
  { key: 'origin-sby2', label: 'Yogyakarta', code: 'JOG' },
  { key: 'origin-solo', label: 'Solo', code: 'SOC' },
  { key: 'origin-smg', label: 'Semarang', code: 'SRG' },
  { key: 'origin-other', label: 'Kota Lain', code: '...' },
];

const hotelPreferences = [
  { key: 'hp-near-haram', id: 'near-haram', label: 'Near Haram', cat: 'Lokasi' },
  { key: 'hp-haram-view', id: 'haram-view', label: 'Haram View', cat: 'Lokasi' },
  { key: 'hp-kaaba-view', id: 'kaaba-view', label: 'Kaaba View', cat: 'Lokasi' },
  { key: 'hp-nabawi-view', id: 'nabawi-view', label: 'Nabawi View', cat: 'Lokasi' },
  { key: 'hp-family', id: 'family-friendly', label: 'Family Friendly', cat: 'Fasilitas' },
  { key: 'hp-elderly', id: 'elderly-friendly', label: 'Elderly Friendly', cat: 'Fasilitas' },
  { key: 'hp-suite', id: 'suite', label: 'Suite', cat: 'Tipe Kamar' },
  { key: 'hp-connecting', id: 'connecting-room', label: 'Connecting Room', cat: 'Tipe Kamar' },
];

const flightOptions = [
  { key: 'fl-economy', id: 'economy', label: 'Economy Class', desc: 'Nyaman dan terjangkau', icon: '💺' },
  { key: 'fl-business', id: 'business', label: 'Business Class', desc: 'Kenyamanan premium', icon: '🛋️' },
  { key: 'fl-direct', id: 'direct', label: 'Direct Flight', desc: 'Tanpa transit', icon: '✈️' },
  { key: 'fl-value', id: 'best-value', label: 'Best Value', desc: 'Harga terbaik', icon: '💰' },
];

const flightArrangement = [
  { key: 'fa-safaruna', id: 'SAFARUNA_ARRANGES', label: 'SAFARUNA Atur', desc: 'Kami carikan penerbangan terbaik untuk Anda' },
  { key: 'fa-own', id: 'OWN_FLIGHT', label: 'Sudah Punya Tiket', desc: 'Saya sudah memiliki tiket penerbangan' },
  { key: 'fa-help', id: 'HELP_CHOOSE', label: 'Bantu Saya Pilih', desc: 'Konsultasikan pilihan penerbangan dengan Concierge' },
];

const transportOptions = [
  { key: 'tr-private', id: 'PRIVATE', label: 'Private', desc: 'Kendaraan eksklusif untuk grup Anda', icon: '🚗' },
  { key: 'tr-family', id: 'FAMILY', label: 'Family', desc: 'Minibus untuk keluarga besar', icon: '🚐' },
  { key: 'tr-group', id: 'GROUP', label: 'Group', desc: 'Bergabung dengan jamaah lain', icon: '🚌' },
  { key: 'tr-premium', id: 'PREMIUM', label: 'Premium', desc: 'SUV atau sedan mewah', icon: '🏎️' },
  { key: 'tr-wheelchair', id: 'WHEELCHAIR', label: 'Wheelchair Accessible', desc: 'Kendaraan ramah kursi roda', icon: '♿' },
];

const priorityOptions = [
  { key: 'pr-close-haram', id: 'close-haram', label: 'Dekat Haram', icon: '🕌' },
  { key: 'pr-haram-view', id: 'haram-view-prio', label: 'Haram View', icon: '👁️' },
  { key: 'pr-comfort', id: 'comfort-hotel', label: 'Hotel Nyaman', icon: '🏨' },
  { key: 'pr-private-trans', id: 'private-transport', label: 'Transport Private', icon: '🚗' },
  { key: 'pr-elderly', id: 'elderly-assistance', label: 'Bantuan Lansia', icon: '👴' },
  { key: 'pr-child', id: 'child-friendly', label: 'Ramah Anak', icon: '👶' },
  { key: 'pr-flexible', id: 'flexible-itinerary', label: 'Itinerary Fleksibel', icon: '📅' },
  { key: 'pr-premium', id: 'premium-experience', label: 'Premium Experience', icon: '⭐' },
  { key: 'pr-spiritual', id: 'spiritual-focus', label: 'Fokus Spiritual', icon: '🤲' },
  { key: 'pr-ziyarat', id: 'ziyarat', label: 'Ziyarat', icon: '🗺️' },
];

const budgets = [
  { key: 'budget-30', id: '<30', label: '< Rp 30 juta', sub: 'per pax' },
  { key: 'budget-30-40', id: '30-40', label: 'Rp 30 – 40 juta', sub: 'per pax' },
  { key: 'budget-40-50', id: '40-50', label: 'Rp 40 – 50 juta', sub: 'per pax' },
  { key: 'budget-50-75', id: '50-75', label: 'Rp 50 – 75 juta', sub: 'per pax' },
  { key: 'budget-75+', id: '>75', label: '> Rp 75 juta', sub: 'per pax' },
  { key: 'budget-custom', id: 'custom', label: 'Custom Budget', sub: 'Saya tentukan sendiri' },
];

const recommendations = [
  {
    key: 'rec-smart',
    type: 'SMART',
    label: 'Smart',
    tag: 'Best Value',
    tagColor: 'bg-blue-100 text-blue-700',
    hotel_makkah: 'Hilton Makkah Convention',
    hotel_madinah: 'Pullman Zamzam Madinah',
    room_makkah: 'Deluxe Room',
    room_madinah: 'Superior Room',
    flight: 'Saudia SV818',
    cabin: 'Ekonomi',
    transport: 'Shared Hiace',
    visa: 'Visa Umrah Reguler',
    guide: 'Muttawif Grup',
    ziyarat: 'Termasuk',
    price: 'Rp 38.500.000',
    priceNum: 38500000,
    stars: 4,
    highlighted: false,
  },
  {
    key: 'rec-signature',
    type: 'SIGNATURE',
    label: 'Signature',
    tag: 'Direkomendasikan',
    tagColor: 'bg-accent text-accent-foreground',
    hotel_makkah: 'Swissôtel Al Maqam Makkah',
    hotel_madinah: 'Anwar Al Madinah Mövenpick',
    room_makkah: 'Superior Room',
    room_madinah: 'Deluxe Room',
    flight: 'Saudia SV818',
    cabin: 'Ekonomi+',
    transport: 'Private Toyota Hiace',
    visa: 'Visa Umrah + Siskopatuh',
    guide: 'Muttawif Semi-Private',
    ziyarat: 'Termasuk',
    price: 'Rp 54.800.000',
    priceNum: 54800000,
    stars: 5,
    highlighted: true,
  },
  {
    key: 'rec-private',
    type: 'PRIVATE',
    label: 'Private',
    tag: 'Best Experience',
    tagColor: 'bg-primary/10 text-primary',
    hotel_makkah: 'Fairmont Makkah Clock Tower',
    hotel_madinah: 'Oberoi Madinah',
    room_makkah: 'Deluxe Suite',
    room_madinah: 'Premier Suite',
    flight: 'Saudia SV818',
    cabin: 'Business Class',
    transport: 'Private GMC Yukon',
    visa: 'Visa Umrah + Fast Track',
    guide: 'Muttawif Privat Eksklusif',
    ziyarat: 'Termasuk',
    price: 'Rp 89.200.000',
    priceNum: 89200000,
    stars: 5,
    highlighted: false,
  },
];

interface BuilderState {
  // Step 1
  adults: number;
  children: number;
  infants: number;
  special: string[];
  // Step 2
  journeyType: string;
  // Step 3
  departure: string;
  returnDate: string;
  // Step 4
  origin: string;
  makkahNightsOverride: string;
  madinahNightsOverride: string;
  style: string;
  hotelPreferences: string[];
  // Step 5
  flightArrangement: string;
  flightPreferences: string[];
  // Step 6
  transport: string;
  // Step 7
  priorities: string[];
  budget: string;
  customBudget: string;
  // Step 8
  specialRequests: string;
}

interface CustomerDetails {
  name: string;
  whatsapp: string;
  email: string;
  notes: string;
}

const STORAGE_KEY = 'safaruna_journey_builder_v2';

const generateJourneyId = () => {
  const year = new Date().getFullYear();
  const seq = String(Math.floor(Math.random() * 99999) + 1).padStart(5, '0');
  return `SF-${year}-${seq}`;
};

const formatRupiah = (num: number) => 'Rp ' + num.toLocaleString('id-ID');

const getJourneyTypeLabel = (id: string) => journeyTypes.find((j) => j.id === id)?.label || id;
const getFlightArrangementLabel = (id: string) => flightArrangement.find((f) => f.id === id)?.label || id;
const getTransportLabel = (id: string) => transportOptions.find((t) => t.id === id)?.label || id;

export default function JourneyBuilderClient() {
  const [step, setStep] = useState(1);
  const [selectedRec, setSelectedRec] = useState<string | null>(null);
  const [journeyId] = useState(generateJourneyId);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const [state, setState] = useState<BuilderState>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) return JSON.parse(saved);
      } catch {}
    }
    return {
      adults: 2, children: 0, infants: 0, special: [],
      journeyType: '',
      departure: '', returnDate: '',
      origin: '', makkahNightsOverride: '', madinahNightsOverride: '', style: '', hotelPreferences: [],
      flightArrangement: '', flightPreferences: [],
      transport: '',
      priorities: [], budget: '', customBudget: '',
      specialRequests: '',
    };
  });

  const [customer, setCustomer] = useState<CustomerDetails>({
    name: '', whatsapp: '', email: '', notes: '',
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
  }, [state]);

  const totalPax = state.adults + state.children + state.infants;

  const getDuration = useCallback(() => {
    if (!state.departure || !state.returnDate) return null;
    const dep = new Date(state.departure);
    const ret = new Date(state.returnDate);
    const diff = Math.round((ret.getTime() - dep.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : null;
  }, [state.departure, state.returnDate]);

  const duration = getDuration();
  const makkahNights = duration ? Math.ceil(duration * 0.6) : null;
  const madinahNights = duration ? Math.floor(duration * 0.4) : null;

  const prevDurationRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (duration && duration !== prevDurationRef.current) {
      const autoMakkah = Math.ceil(duration * 0.6);
      const autoMadinah = Math.floor(duration * 0.4);
      setState((s) => ({
        ...s,
        makkahNightsOverride: String(autoMakkah),
        madinahNightsOverride: String(autoMadinah),
      }));
    }
    if (!duration) {
      setState((s) => ({ ...s, makkahNightsOverride: '', madinahNightsOverride: '' }));
    }
    prevDurationRef.current = duration;
  }, [duration]);

  const effectiveMakkahNights = state.makkahNightsOverride !== '' ? parseInt(state.makkahNightsOverride, 10) || 0 : makkahNights;
  const effectiveMadinahNights = state.madinahNightsOverride !== '' ? parseInt(state.madinahNightsOverride, 10) || 0 : madinahNights;
  const originLabel = origins.find((o) => o.key === state.origin)?.label || 'Indonesia';
  const selectedRecData = recommendations.find((r) => r.key === selectedRec) || recommendations[1];

  const toggleArr = (field: keyof BuilderState, id: string) => {
    setState((s) => {
      const arr = (s[field] as string[]) || [];
      return {
        ...s,
        [field]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id],
      };
    });
  };

  const canProceed = () => {
    if (step === 1) return state.adults >= 1;
    if (step === 2) return state.journeyType !== '';
    if (step === 3) return !!(state.departure && state.returnDate);
    if (step === 4) return state.origin !== '' && state.style !== '';
    if (step === 5) return state.flightArrangement !== '';
    if (step === 6) return state.transport !== '';
    if (step === 7) return state.budget !== '';
    if (step === 8) return true;
    return true;
  };

  const handleNext = () => {
    if (step < 9) setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const handleReset = () => {
    setState({
      adults: 2, children: 0, infants: 0, special: [],
      journeyType: '',
      departure: '', returnDate: '',
      origin: '', makkahNightsOverride: '', madinahNightsOverride: '', style: '', hotelPreferences: [],
      flightArrangement: '', flightPreferences: [],
      transport: '',
      priorities: [], budget: '', customBudget: '',
      specialRequests: '',
    });
    setSelectedRec(null);
    setPdfSuccess(false);
    setSendSuccess(false);
    setStep(1);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  const validateCustomer = () => {
    if (!customer.name.trim()) return 'Nama lengkap wajib diisi.';
    if (!customer.whatsapp.trim()) return 'Nomor WhatsApp wajib diisi.';
    if (customer.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) return 'Format email tidak valid.';
    return '';
  };

  const handleExportPDF = async () => {
    const err = validateCustomer();
    if (err) { setFormError(err); return; }
    setFormError('');
    setPdfLoading(true);
    try {
      const jsPDFModule = await import('jspdf').catch(() => null);
      if (!jsPDFModule) { generatePrintablePDF(); return; }
      const { jsPDF } = jsPDFModule;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const rec = selectedRecData;
      const pageW = 210;
      const margin = 20;
      const contentW = pageW - margin * 2;

      // ── COVER PAGE ──────────────────────────────────────────────
      doc.setFillColor(15, 74, 55);
      doc.rect(0, 0, pageW, 297, 'F');
      // Gold left bar
      doc.setFillColor(212, 175, 55);
      doc.rect(0, 0, 8, 297, 'F');
      // Gold top accent line
      doc.setFillColor(212, 175, 55);
      doc.rect(8, 0, pageW - 8, 3, 'F');

      // Brand
      doc.setTextColor(212, 175, 55);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('SAFARUNA', margin, 35);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(255, 255, 255);
      doc.text('Private Umrah & Muslim Journey Concierge', margin, 43);

      // Divider
      doc.setDrawColor(212, 175, 55);
      doc.setLineWidth(0.3);
      doc.line(margin, 52, pageW - margin, 52);

      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('PRIVATE JOURNEY BRIEF', margin, 75);
      doc.setTextColor(212, 175, 55);
      doc.setFontSize(26);
      doc.setFont('helvetica', 'bold');
      doc.text('Rancangan', margin, 90);
      doc.text('Perjalanan Anda', margin, 104);

      doc.setDrawColor(212, 175, 55);
      doc.setLineWidth(0.5);
      doc.line(margin, 115, margin + 80, 115);

      // Customer info box
      doc.setFillColor(255, 255, 255);
      doc.setFillColor(0, 0, 0);
      doc.setGState && doc.setGState(doc.GState({ opacity: 0.15 }));
      doc.rect(margin, 125, contentW, 55, 'F');
      doc.setGState && doc.setGState(doc.GState({ opacity: 1 }));

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(212, 175, 55);
      doc.text('DISIAPKAN UNTUK', margin + 5, 137);
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(customer.name || 'Pelanggan SAFARUNA', margin + 5, 148);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(255, 255, 255);
      doc.text(`Journey ID: ${journeyId}`, margin + 5, 158);
      doc.text(`Tanggal Dibuat: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin + 5, 166);
      doc.text(`Tipe Journey: ${rec.label} — ${getJourneyTypeLabel(state.journeyType)}`, margin + 5, 174);

      // Bottom footer on cover
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      doc.text('Dokumen ini adalah rancangan awal. Harga final dikonfirmasi oleh Concierge SAFARUNA.', margin, 270);
      doc.setTextColor(212, 175, 55);
      doc.text('concierge@safaruna.id  ·  +62 812-3456-7890  ·  safaruna.id', margin, 278);

      // ── PAGE 2: RINGKASAN PERJALANAN ────────────────────────────
      doc.addPage();
      addPageHeader(doc, 'RINGKASAN PERJALANAN', journeyId, pageW, margin);
      let y = 50;

      // Two-column summary grid
      const summaryLeft = [
        ['Nama Jamaah', customer.name || '-'],
        ['Nomor WhatsApp', customer.whatsapp || '-'],
        ['Email', customer.email || '-'],
        ['Jumlah Jamaah', `${totalPax} Pax`],
        ['Dewasa / Anak / Bayi', `${state.adults} / ${state.children} / ${state.infants}`],
      ];
      const summaryRight = [
        ['Jenis Perjalanan', getJourneyTypeLabel(state.journeyType)],
        ['Gaya Perjalanan', state.style || '-'],
        ['Kota Asal', originLabel],
        ['Tanggal Berangkat', state.departure ? new Date(state.departure).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'],
        ['Tanggal Kembali', state.returnDate ? new Date(state.returnDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'],
      ];

      // Section header
      doc.setFillColor(240, 247, 244);
      doc.rect(margin, y - 5, contentW, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 74, 55);
      doc.text('DATA JAMAAH & PERJALANAN', margin + 3, y + 1);
      y += 12;

      const colW = contentW / 2 - 5;
      summaryLeft.forEach(([label, value], i) => {
        const rightItem = summaryRight[i];
        // Left col
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text(label, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(String(value), margin, y + 5);
        // Right col
        if (rightItem) {
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(120, 120, 120);
          doc.text(rightItem[0], margin + colW + 10, y);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(30, 30, 30);
          doc.text(String(rightItem[1]), margin + colW + 10, y + 5);
        }
        y += 16;
      });

      // Duration box
      y += 3;
      doc.setFillColor(15, 74, 55);
      doc.rect(margin, y, contentW, 22, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`TOTAL DURASI: ${duration || '-'} HARI`, margin + 5, y + 8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Malam Makkah: ${effectiveMakkahNights || '-'}  ·  Malam Madinah: ${effectiveMadinahNights || '-'}`, margin + 5, y + 16);
      y += 30;

      // Kebutuhan khusus
      if (state.special.length > 0) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text('KEBUTUHAN KHUSUS', margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(state.special.join(', '), margin, y + 6);
        y += 16;
      }

      // ── PAGE 3: AKOMODASI ───────────────────────────────────────
      doc.addPage();
      addPageHeader(doc, 'AKOMODASI', journeyId, pageW, margin);
      y = 50;

      // Makkah
      doc.setFillColor(240, 247, 244);
      doc.rect(margin, y - 5, contentW, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 74, 55);
      doc.text('🕌  HOTEL MAKKAH', margin + 3, y + 1);
      y += 12;
      const makkahRows = [
        ['Hotel', rec.hotel_makkah],
        ['Tipe Kamar', rec.room_makkah],
        ['Jumlah Malam', effectiveMakkahNights ? `${effectiveMakkahNights} Malam` : '-'],
        ['Bintang', `${rec.stars} Bintang`],
        ['Lokasi', 'Dekat Masjidil Haram, Makkah Al-Mukarramah'],
      ];
      makkahRows.forEach(([label, value]) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text(label, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(String(value), margin + 50, y);
        y += 9;
      });

      // Hotel preferences
      if (state.hotelPreferences.length > 0) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text('Preferensi Hotel', margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(state.hotelPreferences.join(', '), margin + 50, y);
        y += 9;
      }

      y += 8;
      // Madinah
      doc.setFillColor(240, 247, 244);
      doc.rect(margin, y - 5, contentW, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 74, 55);
      doc.text('🕌  HOTEL MADINAH', margin + 3, y + 1);
      y += 12;
      const madinahRows = [
        ['Hotel', rec.hotel_madinah],
        ['Tipe Kamar', rec.room_madinah],
        ['Jumlah Malam', effectiveMadinahNights ? `${effectiveMadinahNights} Malam` : '-'],
        ['Bintang', `${rec.stars} Bintang`],
        ['Lokasi', 'Dekat Masjid Nabawi, Madinah Al-Munawwarah'],
      ];
      madinahRows.forEach(([label, value]) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text(label, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(String(value), margin + 50, y);
        y += 9;
      });

      // ── PAGE 4: PENERBANGAN & TRANSPORT ─────────────────────────
      doc.addPage();
      addPageHeader(doc, 'PENERBANGAN & TRANSPORTASI', journeyId, pageW, margin);
      y = 50;

      doc.setFillColor(240, 247, 244);
      doc.rect(margin, y - 5, contentW, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 74, 55);
      doc.text('✈️  PENERBANGAN', margin + 3, y + 1);
      y += 12;
      const flightRows = [
        ['Maskapai', rec.flight],
        ['Kelas Kabin', rec.cabin],
        ['Rute', `${originLabel} → Jeddah / Madinah`],
        ['Keberangkatan', state.departure ? new Date(state.departure).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'],
        ['Kepulangan', state.returnDate ? new Date(state.returnDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'],
        ['Bagasi', rec.cabin === 'Business Class' ? '40 kg' : '30 kg'],
        ['Pengaturan', getFlightArrangementLabel(state.flightArrangement)],
      ];
      if (state.flightPreferences.length > 0) {
        flightRows.push(['Preferensi', state.flightPreferences.join(', ')]);
      }
      flightRows.forEach(([label, value]) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text(label, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(String(value), margin + 50, y);
        y += 9;
      });

      y += 8;
      doc.setFillColor(240, 247, 244);
      doc.rect(margin, y - 5, contentW, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 74, 55);
      doc.text('🚗  TRANSPORTASI DARAT', margin + 3, y + 1);
      y += 12;
      const transportRows = [
        ['Tipe Transport', getTransportLabel(state.transport)],
        ['Kendaraan', rec.transport],
        ['Rute', 'Bandara → Hotel → Masjid → Bandara'],
      ];
      transportRows.forEach(([label, value]) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text(label, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(String(value), margin + 50, y);
        y += 9;
      });

      // ── PAGE 5: LAYANAN & ESTIMASI HARGA ────────────────────────
      doc.addPage();
      addPageHeader(doc, 'LAYANAN & ESTIMASI HARGA', journeyId, pageW, margin);
      y = 50;

      doc.setFillColor(240, 247, 244);
      doc.rect(margin, y - 5, contentW, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 74, 55);
      doc.text('🛎️  LAYANAN PERJALANAN', margin + 3, y + 1);
      y += 12;
      const serviceRows = [
        ['Visa', rec.visa],
        ['Muttawif / Guide', rec.guide],
        ['Ziyarat', rec.ziyarat],
        ['Prioritas', state.priorities.length > 0 ? state.priorities.slice(0, 4).join(', ') : 'Tidak ada'],
      ];
      serviceRows.forEach(([label, value]) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text(label, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        const lines = doc.splitTextToSize(String(value), contentW - 55);
        doc.text(lines, margin + 50, y);
        y += Math.max(9, lines.length * 5 + 4);
      });

      y += 8;
      // Pricing
      doc.setFillColor(240, 247, 244);
      doc.rect(margin, y - 5, contentW, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 74, 55);
      doc.text('💰  ESTIMASI HARGA', margin + 3, y + 1);
      y += 12;

      const priceItems = [
        ['Penerbangan', formatRupiah(Math.round(rec.priceNum * 0.35))],
        ['Hotel Makkah', formatRupiah(Math.round(rec.priceNum * 0.25))],
        ['Hotel Madinah', formatRupiah(Math.round(rec.priceNum * 0.18))],
        ['Visa Umrah', formatRupiah(Math.round(rec.priceNum * 0.05))],
        ['Transport', formatRupiah(Math.round(rec.priceNum * 0.07))],
        ['Muttawif & Guide', formatRupiah(Math.round(rec.priceNum * 0.06))],
        ['Add-ons & Layanan', formatRupiah(Math.round(rec.priceNum * 0.04))],
      ];

      doc.setFillColor(250, 250, 250);
      doc.rect(margin, y - 3, contentW, priceItems.length * 9 + 6, 'F');
      priceItems.forEach(([label, value]) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        doc.text(label, margin + 5, y);
        doc.text(value, pageW - margin - 5, y, { align: 'right' });
        y += 9;
      });

      doc.setDrawColor(15, 74, 55);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y);
      y += 7;

      doc.setFillColor(15, 74, 55);
      doc.rect(margin, y - 4, contentW, 14, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('ESTIMASI TOTAL / PAX', margin + 5, y + 5);
      doc.setTextColor(212, 175, 55);
      doc.text(rec.price, pageW - margin - 5, y + 5, { align: 'right' });
      y += 18;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Total ${totalPax} Pax: ${formatRupiah(rec.priceNum * totalPax)}`, margin + 5, y);
      y += 8;
      doc.setFontSize(7.5);
      doc.setTextColor(150, 150, 150);
      const disclaimer = 'Estimasi berdasarkan pilihan Anda. Harga final akan dikonfirmasi oleh Concierge SAFARUNA setelah konsultasi dan pengecekan ketersediaan aktual.';
      const disclaimerLines = doc.splitTextToSize(disclaimer, contentW);
      doc.text(disclaimerLines, margin, y);

      // ── PAGE 6: CATATAN & PERMINTAAN KHUSUS ─────────────────────
      doc.addPage();
      addPageHeader(doc, 'CATATAN & PERMINTAAN KHUSUS', journeyId, pageW, margin);
      y = 50;

      if (state.specialRequests) {
        doc.setFillColor(240, 247, 244);
        doc.rect(margin, y - 5, contentW, 8, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 74, 55);
        doc.text('PERMINTAAN KHUSUS ANDA', margin + 3, y + 1);
        y += 12;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        const reqLines = doc.splitTextToSize(state.specialRequests, contentW);
        doc.text(reqLines, margin, y);
        y += reqLines.length * 6 + 12;
      }

      if (customer.notes) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120, 120, 120);
        doc.text('CATATAN TAMBAHAN', margin, y);
        y += 7;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        const noteLines = doc.splitTextToSize(customer.notes, contentW);
        doc.text(noteLines, margin, y);
        y += noteLines.length * 6 + 12;
      }

      // Standard notes
      y += 5;
      doc.setFillColor(255, 251, 235);
      doc.rect(margin, y - 3, contentW, 50, 'F');
      doc.setDrawColor(212, 175, 55);
      doc.setLineWidth(0.3);
      doc.rect(margin, y - 3, contentW, 50, 'S');
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(120, 80, 0);
      doc.text('CATATAN PENTING', margin + 5, y + 5);
      const stdNotes = [
        '• Dokumen ini adalah rancangan awal berdasarkan preferensi Anda.',
        '• Harga yang tercantum adalah estimasi dan dapat berubah sesuai ketersediaan.',
        '• Harga final akan dikonfirmasi dalam quotation resmi dari SAFARUNA.',
        '• Komponen perjalanan dapat disesuaikan setelah konsultasi dengan Concierge.',
        '• Visa Umrah tunduk pada regulasi dan kuota dari Kementerian Haji Arab Saudi.',
      ];
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 60, 0);
      doc.setFontSize(8);
      let noteY = y + 13;
      stdNotes.forEach((note) => {
        doc.text(note, margin + 5, noteY);
        noteY += 7;
      });

      // ── FINAL PAGE: CTA ──────────────────────────────────────────
      doc.addPage();
      doc.setFillColor(15, 74, 55);
      doc.rect(0, 0, pageW, 297, 'F');
      doc.setFillColor(212, 175, 55);
      doc.rect(0, 0, 8, 297, 'F');
      doc.setFillColor(212, 175, 55);
      doc.rect(8, 0, pageW - 8, 3, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('LANGKAH SELANJUTNYA', margin, 80);
      doc.setTextColor(212, 175, 55);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Siap Mewujudkan', margin, 96);
      doc.text('Perjalanan Anda?', margin, 112);

      doc.setDrawColor(212, 175, 55);
      doc.setLineWidth(0.5);
      doc.line(margin, 122, margin + 80, 122);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(255, 255, 255);
      doc.text('Hubungi Concierge SAFARUNA untuk:', margin, 138);
      const ctaPoints = [
        '✓  Konfirmasi ketersediaan dan harga aktual',
        '✓  Penyesuaian komponen perjalanan',
        '✓  Quotation resmi dengan detail lengkap',
        '✓  Proses booking dan pembayaran',
      ];
      let ctaY = 150;
      ctaPoints.forEach((pt) => {
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.text(pt, margin, ctaY);
        ctaY += 10;
      });

      doc.setFillColor(212, 175, 55);
      doc.rect(margin, ctaY + 8, contentW, 28, 'F');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 74, 55);
      doc.text('WhatsApp: +62 856-4750-9693', margin + 10, ctaY + 20);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('concierge@safaruna.id  ·  safaruna.id', margin + 10, ctaY + 30);

      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      doc.text(`Journey ID: ${journeyId}`, margin, 270);
      doc.text('Prepared by SAFARUNA — Private Umrah & Muslim Journey Concierge', margin, 278);

      const fileName = `SAFARUNA_Journey_${(customer.name || 'Customer').replace(/\s+/g, '_')}_${journeyId}.pdf`;
      doc.save(fileName);
      setPdfSuccess(true);
    } catch (e) {
      generatePrintablePDF();
    } finally {
      setPdfLoading(false);
    }
  };

  const generatePrintablePDF = () => {
    const rec = selectedRecData;
    const printContent = `<!DOCTYPE html><html><head><title>SAFARUNA Journey Brief — ${journeyId}</title><style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:Arial,sans-serif;color:#1a1a1a;background:#fff}
      .cover{background:#0f4a37;color:#fff;padding:40px;min-height:200px;position:relative}
      .cover-bar{position:absolute;left:0;top:0;bottom:0;width:8px;background:#d4af37}
      .cover h1{color:#d4af37;font-size:28px;margin-bottom:4px}
      .cover .sub{opacity:.7;font-size:12px;margin-bottom:20px}
      .cover .customer{font-size:16px;font-weight:bold;margin-top:16px}
      .cover .jid{font-size:11px;opacity:.6;margin-top:4px}
      .body{padding:24px}
      .section{margin-bottom:20px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden}
      .section-title{background:#f0f7f4;padding:10px 16px;font-weight:bold;font-size:13px;color:#0f4a37;border-bottom:1px solid #e5e7eb}
      .row{display:flex;padding:8px 16px;border-top:1px solid #f3f4f6}
      .label{width:160px;color:#6b7280;font-size:12px;flex-shrink:0}
      .value{font-size:12px;font-weight:500;flex:1}
      .price-total{background:#0f4a37;color:#fff;padding:12px 16px;display:flex;justify-content:space-between;align-items:center}
      .price-total .amount{color:#d4af37;font-size:18px;font-weight:bold}
      .disclaimer{background:#fffbeb;border:1px solid #fcd34d;border-radius:8px;padding:12px 16px;margin-top:16px;font-size:11px;color:#92400e}
      .footer{text-align:center;margin-top:24px;padding:20px;background:#f9fafb;border-radius:8px}
      @media print{body{padding:0}.body{padding:16px}}
    </style></head><body>
    <div class="cover"><div class="cover-bar"></div>
      <h1>SAFARUNA</h1><p class="sub">Private Umrah & Muslim Journey Concierge</p>
      <p style="color:#fff;font-size:11px;opacity:.7">PRIVATE JOURNEY BRIEF</p>
      <p class="customer">${customer.name || 'Pelanggan SAFARUNA'}</p>
      <p class="jid">Journey ID: ${journeyId} · Dibuat: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
    </div>
    <div class="body">
      <div class="section"><div class="section-title">RINGKASAN PERJALANAN</div>
        <div class="row"><span class="label">Jenis Perjalanan</span><span class="value">${getJourneyTypeLabel(state.journeyType)}</span></div>
        <div class="row"><span class="label">Gaya Perjalanan</span><span class="value">${state.style || '-'}</span></div>
        <div class="row"><span class="label">Jumlah Jamaah</span><span class="value">${totalPax} Pax (${state.adults} Dewasa${state.children ? ', ' + state.children + ' Anak' : ''}${state.infants ? ', ' + state.infants + ' Bayi' : ''})</span></div>
        <div class="row"><span class="label">Keberangkatan</span><span class="value">${state.departure ? new Date(state.departure).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</span></div>
        <div class="row"><span class="label">Kepulangan</span><span class="value">${state.returnDate ? new Date(state.returnDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</span></div>
        <div class="row"><span class="label">Durasi</span><span class="value">${duration ? duration + ' Hari' : '-'}</span></div>
        <div class="row"><span class="label">Kota Asal</span><span class="value">${originLabel}</span></div>
        <div class="row"><span class="label">Tipe Journey</span><span class="value">${rec.label}</span></div>
      </div>
      <div class="section"><div class="section-title">HOTEL MAKKAH</div>
        <div class="row"><span class="label">Hotel</span><span class="value">${rec.hotel_makkah}</span></div>
        <div class="row"><span class="label">Tipe Kamar</span><span class="value">${rec.room_makkah}</span></div>
        <div class="row"><span class="label">Jumlah Malam</span><span class="value">${effectiveMakkahNights || '-'} Malam</span></div>
      </div>
      <div class="section"><div class="section-title">HOTEL MADINAH</div>
        <div class="row"><span class="label">Hotel</span><span class="value">${rec.hotel_madinah}</span></div>
        <div class="row"><span class="label">Tipe Kamar</span><span class="value">${rec.room_madinah}</span></div>
        <div class="row"><span class="label">Jumlah Malam</span><span class="value">${effectiveMadinahNights || '-'} Malam</span></div>
      </div>
      <div class="section"><div class="section-title">PENERBANGAN</div>
        <div class="row"><span class="label">Maskapai</span><span class="value">${rec.flight}</span></div>
        <div class="row"><span class="label">Kelas Kabin</span><span class="value">${rec.cabin}</span></div>
        <div class="row"><span class="label">Rute</span><span class="value">${originLabel} → Jeddah / Madinah</span></div>
        <div class="row"><span class="label">Pengaturan</span><span class="value">${getFlightArrangementLabel(state.flightArrangement)}</span></div>
      </div>
      <div class="section"><div class="section-title">LAYANAN</div>
        <div class="row"><span class="label">Transport</span><span class="value">${rec.transport}</span></div>
        <div class="row"><span class="label">Visa</span><span class="value">${rec.visa}</span></div>
        <div class="row"><span class="label">Muttawif</span><span class="value">${rec.guide}</span></div>
        ${state.specialRequests ? `<div class="row"><span class="label">Permintaan Khusus</span><span class="value">${state.specialRequests}</span></div>` : ''}
      </div>
      <div class="section"><div class="section-title">ESTIMASI HARGA</div>
        <div class="row"><span class="label">Harga per Pax</span><span class="value">${rec.price}</span></div>
        <div class="price-total"><span>TOTAL ${totalPax} PAX</span><span class="amount">${formatRupiah(rec.priceNum * totalPax)}</span></div>
      </div>
      <div class="disclaimer">⚠️ Estimasi berdasarkan pilihan Anda. Harga final akan dikonfirmasi oleh Concierge SAFARUNA setelah konsultasi dan pengecekan ketersediaan aktual.</div>
      <div class="footer">
        <p style="font-size:13px;font-weight:bold;color:#0f4a37">Hubungi Concierge SAFARUNA</p>
        <p style="font-size:12px;color:#374151;margin-top:4px">WhatsApp: +62 856-4750-9693 · concierge@safaruna.id</p>
        <p style="font-size:10px;color:#9ca3af;margin-top:8px">Journey ID: ${journeyId} · Prepared by SAFARUNA — Private Umrah & Muslim Journey Concierge</p>
      </div>
    </div></body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 500);
    }
    setPdfSuccess(true);
    setPdfLoading(false);
  };

  const handleSendToConcierge = async () => {
    const err = validateCustomer();
    if (err) { setFormError(err); return; }
    setFormError('');
    setSendLoading(true);
    const rec = selectedRecData;
    await new Promise((r) => setTimeout(r, 1000));
    const message = encodeURIComponent(
      `Assalamu'alaikum SAFARUNA Concierge 🕌\n\n` +
      `Saya ingin mengirimkan Journey Brief saya:\n\n` +
      `*Journey ID:* ${journeyId}\n` +
      `*Nama:* ${customer.name}\n` +
      `*WhatsApp:* ${customer.whatsapp}\n` +
      `*Jenis Perjalanan:* ${getJourneyTypeLabel(state.journeyType)}\n` +
      `*Jumlah Jamaah:* ${totalPax} Pax\n` +
      `*Keberangkatan:* ${state.departure ? new Date(state.departure).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}\n` +
      `*Kepulangan:* ${state.returnDate ? new Date(state.returnDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}\n` +
      `*Durasi:* ${duration || '-'} Hari\n` +
      `*Malam Makkah:* ${effectiveMakkahNights || '-'} Malam\n` +
      `*Malam Madinah:* ${effectiveMadinahNights || '-'} Malam\n` +
      `*Kota Asal:* ${originLabel}\n` +
      `*Gaya:* ${state.style}\n` +
      `*Tipe Journey:* ${rec.label}\n` +
      `*Hotel Makkah:* ${rec.hotel_makkah} (${rec.room_makkah})\n` +
      `*Hotel Madinah:* ${rec.hotel_madinah} (${rec.room_madinah})\n` +
      `*Penerbangan:* ${rec.flight} — ${rec.cabin}\n` +
      `*Transport:* ${rec.transport}\n` +
      `*Estimasi Harga:* ${rec.price} / pax\n` +
      `*Total ${totalPax} Pax:* ${formatRupiah(rec.priceNum * totalPax)}\n` +
      (state.specialRequests ? `*Permintaan Khusus:* ${state.specialRequests}\n` : '') +
      (customer.notes ? `*Catatan:* ${customer.notes}\n` : '') +
      `\nMohon bantu proses lebih lanjut. Jazakallah khair 🙏`
    );
    setSendLoading(false);
    setSendSuccess(true);
    window.open(`https://wa.me/6285647509693?text=${message}`, '_blank');
  };

  // ── STEP 9: JOURNEY REVIEW ─────────────────────────────────────
  if (step === 9) {
    return (
      <div className="pt-20 pb-24 lg:pb-12 min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-10 py-8">
          <StepProgress step={9} />

          <div className="text-center mb-8 mt-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
              <Sparkles size={14} />
              Journey Brief Siap
            </div>
            <h1 className="text-2xl lg:text-4xl font-bold text-foreground mb-3">
              Rekomendasi Journey Anda
            </h1>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">
              Berdasarkan preferensi Anda — {totalPax} jamaah dari {originLabel}, {duration} hari.
            </p>
          </div>

          {/* Recommendation Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {recommendations.map((rec) => (
              <div
                key={rec.key}
                onClick={() => setSelectedRec(rec.key)}
                className={`card-base overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer ${
                  rec.highlighted ? 'ring-2 ring-accent shadow-lg shadow-accent/10' : ''
                } ${selectedRec === rec.key ? 'ring-2 ring-primary shadow-lg' : ''}`}
              >
                {rec.highlighted && (
                  <div className="bg-gold-gradient px-4 py-2 text-center">
                    <p className="text-accent-foreground text-xs font-bold uppercase tracking-widest">⭐ Paling Direkomendasikan</p>
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">{rec.label}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: rec.stars }).map((_, si) => (
                          <Star key={`star-${rec.key}-${si}`} size={11} className="text-accent fill-accent" />
                        ))}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${rec.tagColor}`}>{rec.tag}</span>
                  </div>

                  <div className="space-y-2.5 mb-5">
                    {[
                      { key: `hm-${rec.key}`, icon: <Hotel size={13} />, label: 'Hotel Makkah', value: rec.hotel_makkah },
                      { key: `hmd-${rec.key}`, icon: <Hotel size={13} />, label: 'Hotel Madinah', value: rec.hotel_madinah },
                      { key: `fl-${rec.key}`, icon: <Plane size={13} />, label: 'Penerbangan', value: `${rec.flight} — ${rec.cabin}` },
                      { key: `tr-${rec.key}`, icon: <Car size={13} />, label: 'Transport', value: rec.transport },
                      { key: `vi-${rec.key}`, icon: <FileCheck size={13} />, label: 'Visa', value: rec.visa },
                      { key: `gu-${rec.key}`, icon: <UserCheck size={13} />, label: 'Guide', value: rec.guide },
                    ].map((comp) => (
                      <div key={comp.key} className="flex items-start gap-2.5">
                        <span className="text-muted-foreground flex-shrink-0 mt-0.5">{comp.icon}</span>
                        <div className="min-w-0">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{comp.label}</p>
                          <p className="text-xs font-medium text-foreground leading-tight">{comp.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-4 mb-4">
                    <p className="text-xs text-muted-foreground mb-1">Estimasi Harga</p>
                    <p className="text-xl font-bold text-foreground font-mono-data">{rec.price}</p>
                    <p className="text-xs text-muted-foreground">per pax · {totalPax} pax = {formatRupiah(rec.priceNum * totalPax)}</p>
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedRec(rec.key); }}
                    className={`w-full py-2.5 rounded-xl text-xs font-semibold transition-all duration-150 active:scale-95 ${
                      selectedRec === rec.key
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                    }`}
                  >
                    {selectedRec === rec.key ? '✓ Dipilih' : 'Pilih Journey Ini'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="max-w-2xl mx-auto mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex gap-3">
            <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 leading-relaxed">
              Estimasi berdasarkan pilihan Anda. Harga final akan dikonfirmasi oleh Concierge SAFARUNA setelah konsultasi dan pengecekan ketersediaan.
            </p>
          </div>

          {/* Journey Summary */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="card-base p-5">
              <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                <CheckCircle size={16} className="text-primary" />
                Ringkasan Journey Anda
              </h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                {[
                  { label: 'Jenis Perjalanan', value: getJourneyTypeLabel(state.journeyType) },
                  { label: 'Gaya', value: state.style },
                  { label: 'Jamaah', value: `${totalPax} Pax` },
                  { label: 'Kota Asal', value: originLabel },
                  { label: 'Berangkat', value: state.departure ? new Date(state.departure).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-' },
                  { label: 'Kembali', value: state.returnDate ? new Date(state.returnDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-' },
                  { label: 'Durasi', value: duration ? `${duration} Hari` : '-' },
                  { label: 'Makkah / Madinah', value: `${effectiveMakkahNights || '-'} / ${effectiveMadinahNights || '-'} Malam` },
                  { label: 'Penerbangan', value: getFlightArrangementLabel(state.flightArrangement) },
                  { label: 'Transport', value: getTransportLabel(state.transport) },
                ].map((item, i) => (
                  <div key={`sum-${i}`}>
                    <p className="text-muted-foreground">{item.label}</p>
                    <p className="font-semibold text-foreground">{item.value}</p>
                  </div>
                ))}
              </div>
              {state.specialRequests && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Permintaan Khusus</p>
                  <p className="text-xs text-foreground">{state.specialRequests}</p>
                </div>
              )}
              <button
                onClick={() => setStep(8)}
                className="mt-4 text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Edit3 size={12} />
                Edit Journey
              </button>
            </div>
          </div>

          {/* Customer Details & Actions */}
          <div className="max-w-2xl mx-auto animate-fade-in">
            <div className="card-base p-6 mb-5">
              <h3 className="text-lg font-bold text-foreground mb-1">Data Anda</h3>
              <p className="text-muted-foreground text-sm mb-5">Lengkapi data untuk export PDF dan kirim ke Concierge.</p>

              {formError && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-center">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-600">{formError}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="label-base">Nama Lengkap *</label>
                  <div className="relative">
                    <UserCheck size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input type="text" placeholder="Ahmad Farhan" className="input-base pl-9"
                      value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="label-base">Nomor WhatsApp *</label>
                  <div className="relative">
                    <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input type="tel" placeholder="08123456789" className="input-base pl-9"
                      value={customer.whatsapp} onChange={(e) => setCustomer((c) => ({ ...c, whatsapp: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="label-base">Email (opsional)</label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input type="email" placeholder="email@contoh.com" className="input-base pl-9"
                      value={customer.email} onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="label-base">Catatan (opsional)</label>
                  <input type="text" placeholder="Ada kebutuhan khusus?" className="input-base"
                    value={customer.notes} onChange={(e) => setCustomer((c) => ({ ...c, notes: e.target.value }))} />
                </div>
              </div>

              <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 mb-5">
                <p className="text-xs text-primary font-semibold">Journey ID: {journeyId}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {selectedRecData.label} · {selectedRecData.price}/pax · {totalPax} pax = {formatRupiah(selectedRecData.priceNum * totalPax)}
                </p>
              </div>

              {/* 3 Action Buttons per brief */}
              <div className="space-y-3">
                {/* 1. Export PDF */}
                <button
                  onClick={handleExportPDF}
                  disabled={pdfLoading}
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-primary text-primary-foreground font-bold text-sm transition-all duration-150 hover:opacity-90 active:scale-95 disabled:opacity-60"
                >
                  {pdfLoading ? (
                    <><Loader2 size={18} className="animate-spin" /> Memproses PDF...</>
                  ) : pdfSuccess ? (
                    <><CheckCircle size={18} /> PDF Berhasil Dibuat!</>
                  ) : (
                    <><Download size={18} /> Export Journey ke PDF</>
                  )}
                </button>

                {/* 2. Kirim ke Concierge */}
                <button
                  onClick={handleSendToConcierge}
                  disabled={sendLoading}
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-gold-gradient text-accent-foreground font-bold text-sm transition-all duration-150 hover:opacity-90 active:scale-95 disabled:opacity-60"
                >
                  {sendLoading ? (
                    <><Loader2 size={18} className="animate-spin" /> Mengirim ke Concierge...</>
                  ) : sendSuccess ? (
                    <><CheckCircle size={18} /> Terkirim! WhatsApp Terbuka</>
                  ) : (
                    <><Send size={18} /> Kirim ke Concierge</>
                  )}
                </button>

                {/* 3. Edit Journey */}
                <button
                  onClick={() => setStep(1)}
                  className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl border-2 border-border text-foreground font-semibold text-sm transition-all duration-150 hover:border-primary hover:text-primary active:scale-95"
                >
                  <Edit3 size={16} />
                  Edit Journey
                </button>
              </div>

              {sendSuccess && (
                <div className="mt-4 p-4 rounded-xl bg-green-50 border border-green-200 animate-fade-in">
                  <p className="text-sm font-semibold text-green-700 mb-1">✅ Journey berhasil dikirim ke Concierge!</p>
                  <p className="text-xs text-green-600">Tim kami akan menghubungi Anda dalam 1×24 jam untuk finalisasi journey dan quotation resmi.</p>
                </div>
              )}
            </div>
          </div>

          {/* Reset */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <button onClick={() => setStep(8)} className="btn-ghost text-sm">
              <ArrowLeft size={16} />
              Kembali
            </button>
            <button onClick={handleReset} className="btn-ghost text-sm text-muted-foreground">
              <RotateCcw size={16} />
              Mulai Ulang
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP FORM VIEW (Steps 1–8) ─────────────────────────────────
  return (
    <div className="pt-20 pb-24 lg:pb-12 min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-10 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-4">
            Journey Builder
          </span>
          <h1 className="text-2xl lg:text-4xl font-bold text-foreground mb-3">
            Rancang Perjalanan Umrah Anda
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto text-sm">
            Jawab 8 pertanyaan singkat dan kami siapkan rekomendasi journey terbaik untuk Anda.
          </p>
        </div>

        {/* Step Progress */}
        <StepProgress step={step} />

        {/* Progress bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-500"
              style={{ width: `${((step - 1) / 8) * 100}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground text-right mt-1">Langkah {step} dari 9</p>
        </div>

        {/* Step Content */}
        <div className="max-w-2xl mx-auto">
          <div className="card-base p-6 lg:p-8 animate-fade-in" key={`step-content-${step}`}>

            {/* STEP 1: Travelers */}
            {step === 1 && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Siapa yang akan bepergian?</h2>
                <p className="text-muted-foreground text-sm mb-6">Masukkan jumlah jamaah dan kebutuhan khusus.</p>
                <div className="space-y-4 mb-6">
                  {[
                    { key: 'pax-adult', label: 'Dewasa', sub: '18 tahun ke atas', field: 'adults' as const },
                    { key: 'pax-child', label: 'Anak', sub: '2 – 17 tahun', field: 'children' as const },
                    { key: 'pax-infant', label: 'Bayi', sub: 'Di bawah 2 tahun', field: 'infants' as const },
                  ].map((pax) => (
                    <div key={pax.key} className="flex items-center justify-between p-4 rounded-2xl bg-muted/40 border border-border">
                      <div>
                        <p className="font-semibold text-foreground text-sm">{pax.label}</p>
                        <p className="text-muted-foreground text-xs">{pax.sub}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setState((s) => ({ ...s, [pax.field]: Math.max(pax.field === 'adults' ? 1 : 0, s[pax.field] - 1) }))}
                          className="w-9 h-9 rounded-xl border border-border bg-card flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-all active:scale-95 font-bold text-lg"
                        >−</button>
                        <span className="w-8 text-center font-bold text-foreground text-lg font-mono-data">{state[pax.field]}</span>
                        <button
                          onClick={() => setState((s) => ({ ...s, [pax.field]: s[pax.field] + 1 }))}
                          className="w-9 h-9 rounded-xl border border-border bg-card flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-all active:scale-95 font-bold text-lg"
                        >+</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Kebutuhan Khusus</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'sp-elderly', id: 'elderly', label: 'Lansia' },
                      { key: 'sp-wheelchair', id: 'wheelchair', label: 'Kursi Roda' },
                      { key: 'sp-child', id: 'child', label: 'Anak Kecil' },
                      { key: 'sp-special', id: 'special', label: 'Bantuan Khusus' },
                      { key: 'sp-none', id: 'none', label: 'Tidak Ada' },
                    ].map((sp) => (
                      <button key={sp.key}
                        onClick={() => toggleArr('special', sp.id)}
                        className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all duration-150 active:scale-95 ${
                          state.special.includes(sp.id)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card border-border text-foreground hover:border-primary'
                        }`}
                      >{sp.label}</button>
                    ))}
                  </div>
                </div>
                <div className="mt-5 p-4 rounded-xl bg-primary/5 border border-primary/20">
                  <p className="text-sm text-primary font-semibold">Total: {totalPax} Jamaah</p>
                </div>
              </div>
            )}

            {/* STEP 2: Journey Type */}
            {step === 2 && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Jenis perjalanan apa yang Anda inginkan?</h2>
                <p className="text-muted-foreground text-sm mb-6">Pilih jenis perjalanan yang paling sesuai dengan kebutuhan Anda.</p>
                <div className="space-y-3">
                  {journeyTypes.map((jt) => (
                    <button key={jt.key}
                      onClick={() => setState((s) => ({ ...s, journeyType: jt.id }))}
                      className={`w-full p-4 rounded-2xl border text-left transition-all duration-150 active:scale-95 flex items-start gap-4 ${
                        state.journeyType === jt.id
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border bg-card hover:border-primary/50'
                      }`}
                    >
                      <span className="text-2xl flex-shrink-0">{jt.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            state.journeyType === jt.id ? 'border-primary bg-primary' : 'border-border'
                          }`}>
                            {state.journeyType === jt.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                          <p className="font-bold text-foreground text-sm">{jt.label}</p>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed pl-6">{jt.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 3: Dates */}
            {step === 3 && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Kapan rencana keberangkatan?</h2>
                <p className="text-muted-foreground text-sm mb-6">Sistem akan menghitung otomatis malam di Makkah dan Madinah.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="label-base">Tanggal Berangkat</label>
                    <input type="date" className="input-base" value={state.departure}
                      onChange={(e) => setState((s) => ({ ...s, departure: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label-base">Tanggal Kembali</label>
                    <input type="date" className="input-base" value={state.returnDate}
                      onChange={(e) => setState((s) => ({ ...s, returnDate: e.target.value }))} />
                  </div>
                </div>
                {duration && duration > 0 && (
                  <div className="p-5 rounded-2xl bg-primary/5 border border-primary/20 animate-fade-in">
                    <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Kalkulasi Otomatis</p>
                    <p className="text-xs text-muted-foreground mb-4">Nilai Malam Makkah &amp; Madinah dihitung otomatis, namun bisa Anda ubah di langkah berikutnya.</p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-primary font-mono-data">{duration}</p>
                        <p className="text-xs text-muted-foreground mt-1">Total Hari</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-primary font-mono-data">{makkahNights}</p>
                        <p className="text-xs text-muted-foreground mt-1">Malam Makkah</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-primary font-mono-data">{madinahNights}</p>
                        <p className="text-xs text-muted-foreground mt-1">Malam Madinah</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 4: Stay Preferences */}
            {step === 4 && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Preferensi tinggal Anda</h2>
                <p className="text-muted-foreground text-sm mb-6">Pilih kota asal, gaya perjalanan, dan preferensi hotel.</p>

                {/* Origin City */}
                <div className="mb-6">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Kota Keberangkatan</p>
                  <div className="grid grid-cols-3 gap-2">
                    {origins.map((orig) => (
                      <button key={orig.key}
                        onClick={() => setState((s) => ({ ...s, origin: orig.key }))}
                        className={`p-3 rounded-xl border text-left transition-all duration-150 active:scale-95 ${
                          state.origin === orig.key
                            ? 'bg-primary text-primary-foreground border-primary shadow-md'
                            : 'bg-card border-border hover:border-primary text-foreground'
                        }`}
                      >
                        <p className="font-bold text-xs">{orig.label}</p>
                        <p className={`text-[10px] font-mono-data mt-0.5 ${state.origin === orig.key ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{orig.code}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Makkah/Madinah nights editable */}
                {duration && (
                  <div className="mb-6 p-4 rounded-2xl bg-muted/40 border border-border">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Lama Tinggal (dapat diubah)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label-base">Malam Makkah</label>
                        <input type="number" min={0} max={duration}
                          className="input-base text-center font-bold"
                          value={state.makkahNightsOverride}
                          onChange={(e) => setState((s) => ({ ...s, makkahNightsOverride: e.target.value }))} />
                        {makkahNights !== null && state.makkahNightsOverride !== '' && parseInt(state.makkahNightsOverride, 10) !== makkahNights && (
                          <p className="text-xs text-amber-600 mt-1">Saran otomatis: {makkahNights} malam</p>
                        )}
                      </div>
                      <div>
                        <label className="label-base">Malam Madinah</label>
                        <input type="number" min={0} max={duration}
                          className="input-base text-center font-bold"
                          value={state.madinahNightsOverride}
                          onChange={(e) => setState((s) => ({ ...s, madinahNightsOverride: e.target.value }))} />
                        {madinahNights !== null && state.madinahNightsOverride !== '' && parseInt(state.madinahNightsOverride, 10) !== madinahNights && (
                          <p className="text-xs text-amber-600 mt-1">Saran otomatis: {madinahNights} malam</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Journey Style */}
                <div className="mb-6">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Gaya Perjalanan</p>
                  <div className="space-y-2">
                    {journeyStyles.map((js) => (
                      <button key={js.key}
                        onClick={() => setState((s) => ({ ...s, style: js.id }))}
                        className={`w-full p-4 rounded-2xl border text-left transition-all duration-150 active:scale-95 relative ${
                          state.style === js.id
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : `border-border ${js.color} hover:border-primary/50`
                        }`}
                      >
                        {js.recommended && (
                          <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold uppercase">
                            Rekomendasi
                          </span>
                        )}
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            state.style === js.id ? 'border-primary bg-primary' : 'border-border'
                          }`}>
                            {state.style === js.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                          <div>
                            <p className="font-bold text-foreground text-sm">{js.label} <span className="text-xs font-normal text-muted-foreground">— {js.tag}</span></p>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{js.desc}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Hotel Preferences */}
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Preferensi Hotel (opsional)</p>
                  {['Lokasi', 'Fasilitas', 'Tipe Kamar'].map((cat) => (
                    <div key={cat} className="mb-3">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">{cat}</p>
                      <div className="flex flex-wrap gap-2">
                        {hotelPreferences.filter((p) => p.cat === cat).map((pref) => (
                          <button key={pref.key}
                            onClick={() => toggleArr('hotelPreferences', pref.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-all duration-150 active:scale-95 ${
                              state.hotelPreferences.includes(pref.id)
                                ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                : 'bg-card border-border text-foreground hover:border-primary'
                            }`}
                          >
                            {state.hotelPreferences.includes(pref.id) && <CheckCircle size={11} />}
                            {pref.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 5: Flight */}
            {step === 5 && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Preferensi penerbangan</h2>
                <p className="text-muted-foreground text-sm mb-6">Bagaimana Anda ingin mengatur penerbangan?</p>

                <div className="space-y-3 mb-6">
                  {flightArrangement.map((fa) => (
                    <button key={fa.key}
                      onClick={() => setState((s) => ({ ...s, flightArrangement: fa.id }))}
                      className={`w-full p-4 rounded-2xl border text-left transition-all duration-150 active:scale-95 flex items-center gap-4 ${
                        state.flightArrangement === fa.id
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border bg-card hover:border-primary/50'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        state.flightArrangement === fa.id ? 'border-primary bg-primary' : 'border-border'
                      }`}>
                        {state.flightArrangement === fa.id && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      <div>
                        <p className="font-bold text-foreground text-sm">{fa.label}</p>
                        <p className="text-xs text-muted-foreground">{fa.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>

                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Preferensi Kelas & Rute (opsional)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {flightOptions.map((fo) => (
                      <button key={fo.key}
                        onClick={() => toggleArr('flightPreferences', fo.id)}
                        className={`p-3 rounded-xl border text-left transition-all duration-150 active:scale-95 ${
                          state.flightPreferences.includes(fo.id)
                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                            : 'bg-card border-border text-foreground hover:border-primary'
                        }`}
                      >
                        <span className="text-lg">{fo.icon}</span>
                        <p className="font-semibold text-xs mt-1">{fo.label}</p>
                        <p className={`text-[10px] mt-0.5 ${state.flightPreferences.includes(fo.id) ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{fo.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 6: Transport */}
            {step === 6 && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Transportasi darat</h2>
                <p className="text-muted-foreground text-sm mb-6">Pilih tipe transportasi selama di Arab Saudi.</p>
                <div className="space-y-3">
                  {transportOptions.map((tr) => (
                    <button key={tr.key}
                      onClick={() => setState((s) => ({ ...s, transport: tr.id }))}
                      className={`w-full p-4 rounded-2xl border text-left transition-all duration-150 active:scale-95 flex items-center gap-4 ${
                        state.transport === tr.id
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border bg-card hover:border-primary/50'
                      }`}
                    >
                      <span className="text-2xl flex-shrink-0">{tr.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            state.transport === tr.id ? 'border-primary bg-primary' : 'border-border'
                          }`}>
                            {state.transport === tr.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                          <p className="font-bold text-foreground text-sm">{tr.label}</p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 pl-6">{tr.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 7: Priorities & Budget */}
            {step === 7 && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Apa yang paling penting bagi Anda?</h2>
                <p className="text-muted-foreground text-sm mb-6">Pilih prioritas dan estimasi budget per pax.</p>

                <div className="mb-6">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Prioritas Perjalanan (pilih semua yang berlaku)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {priorityOptions.map((pr) => (
                      <button key={pr.key}
                        onClick={() => toggleArr('priorities', pr.id)}
                        className={`p-3 rounded-xl border text-left transition-all duration-150 active:scale-95 flex items-center gap-2 ${
                          state.priorities.includes(pr.id)
                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                            : 'bg-card border-border text-foreground hover:border-primary'
                        }`}
                      >
                        <span className="text-base">{pr.icon}</span>
                        <p className="font-medium text-xs">{pr.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Estimasi Budget per Pax</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {budgets.map((b) => (
                      <button key={b.key}
                        onClick={() => setState((s) => ({ ...s, budget: b.id }))}
                        className={`p-4 rounded-2xl border text-left transition-all duration-150 active:scale-95 ${
                          state.budget === b.id
                            ? 'bg-primary text-primary-foreground border-primary shadow-md'
                            : 'bg-card border-border hover:border-primary text-foreground'
                        }`}
                      >
                        <p className="font-bold text-xs leading-tight">{b.label}</p>
                        <p className={`text-[10px] mt-1 ${state.budget === b.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{b.sub}</p>
                      </button>
                    ))}
                  </div>
                  {state.budget === 'custom' && (
                    <div className="mt-4 animate-fade-in">
                      <label className="label-base">Masukkan Budget (IDR)</label>
                      <input type="text" placeholder="Contoh: 65.000.000" className="input-base"
                        value={state.customBudget}
                        onChange={(e) => setState((s) => ({ ...s, customBudget: e.target.value }))} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 8: Special Requests */}
            {step === 8 && (
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Ada permintaan khusus?</h2>
                <p className="text-muted-foreground text-sm mb-6">Ceritakan kebutuhan spesifik yang belum tercakup di langkah sebelumnya.</p>

                <div className="mb-5">
                  <label className="label-base">Permintaan Khusus (opsional)</label>
                  <textarea
                    rows={5}
                    placeholder="Contoh: Butuh kamar yang berdekatan untuk keluarga, ada anggota keluarga yang menggunakan kursi roda, ingin ziyarat ke Jabal Nur, dll."
                    className="input-base resize-none"
                    value={state.specialRequests}
                    onChange={(e) => setState((s) => ({ ...s, specialRequests: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{state.specialRequests.length}/500 karakter</p>
                </div>

                {/* Journey Summary Preview */}
                <div className="p-4 rounded-2xl bg-primary/5 border border-primary/20">
                  <p className="text-xs font-bold text-primary uppercase tracking-wider mb-3">Ringkasan Journey Anda</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[
                      { label: 'Jenis', value: getJourneyTypeLabel(state.journeyType) },
                      { label: 'Jamaah', value: `${totalPax} Pax` },
                      { label: 'Durasi', value: duration ? `${duration} Hari` : '-' },
                      { label: 'Gaya', value: state.style || '-' },
                      { label: 'Penerbangan', value: getFlightArrangementLabel(state.flightArrangement) },
                      { label: 'Transport', value: getTransportLabel(state.transport) },
                    ].map((item, i) => (
                      <div key={`prev-${i}`}>
                        <p className="text-muted-foreground">{item.label}</p>
                        <p className="font-semibold text-foreground">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 p-3 rounded-xl bg-green-50 border border-green-200 flex gap-2">
                  <CheckCircle size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-700">Setelah ini, kami akan menyiapkan rekomendasi journey terbaik untuk Anda.</p>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-5">
            <button
              onClick={handleBack}
              disabled={step === 1}
              className="btn-ghost disabled:opacity-30"
            >
              <ArrowLeft size={16} />
              Kembali
            </button>
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {step === 8 ? 'Lihat Rekomendasi Journey' : 'Lanjutkan'}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper: add page header to PDF
function addPageHeader(doc: import('jspdf').jsPDF, title: string, journeyId: string, pageW: number, margin: number) {
  doc.setFillColor(15, 74, 55);
  doc.rect(0, 0, pageW, 38, 'F');
  doc.setFillColor(212, 175, 55);
  doc.rect(0, 0, 6, 38, 'F');
  doc.setFillColor(212, 175, 55);
  doc.rect(6, 0, pageW - 6, 2.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, 17);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(212, 175, 55);
  doc.text(`SAFARUNA · ${journeyId}`, margin, 28);
}

// Step Progress Component
function StepProgress({ step }: { step: number }) {
  return (
    <div className="flex items-center justify-center gap-0.5 mb-6 flex-wrap">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-semibold transition-all duration-200 ${
            step === s.num
              ? 'bg-primary text-primary-foreground shadow-md'
              : step > s.num
              ? 'bg-accent text-accent-foreground'
              : 'bg-muted text-muted-foreground'
          }`}>
            {step > s.num ? <CheckCircle size={11} /> : s.icon}
            <span className="hidden sm:inline">{s.label}</span>
            <span className="sm:hidden">{s.num}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`w-3 h-0.5 rounded-full transition-colors duration-200 ${step > s.num ? 'bg-accent' : 'bg-muted'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}